import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { theme, themeOptions, globalStyles, setNexGenThemeMode } from "../theme";
import { authenticate } from "../auth";

// ── Terminal boot sequence lines ──────────────────────────────────────────────
const BOOT_LINES = [
  { t: 0,    cls: "white",  text: "NexGen Cyber Intelligence Platform v1.1.5.1" },
  { t: 60,   cls: "grey",   text: "Copyright (c) 2026 Hassaan Alrifai. All rights reserved." },
  { t: 120,  cls: "grey",   text: "hassaanalrifai.com | contact@hassaanalrifai.com" },
  { t: 200,  cls: "dim",    text: "────────────────────────────────────────────────────" },
  { t: 320,  cls: "green",  text: "[ BOOT ] Initializing system kernel..." },
  { t: 600,  cls: "ok",     text: "[ OK   ] Kernel loaded" },
  { t: 800,  cls: "green",  text: "[ BOOT ] Loading security modules..." },
  { t: 1100, cls: "ok",     text: "[ OK   ] Security policy enforced" },
  { t: 1300, cls: "green",  text: "[ BOOT ] Mounting encrypted volumes..." },
  { t: 1700, cls: "ok",     text: "[ OK   ] All volumes mounted" },
  { t: 1900, cls: "green",  text: "[ BOOT ] Starting network services..." },
  { t: 2200, cls: "ok",     text: "[ OK   ] Network interface active" },
  { t: 2400, cls: "green",  text: "[ BOOT ] Loading analytics engine..." },
  { t: 2700, cls: "ok",     text: "[ OK   ] Analytics engine ready" },
  { t: 2900, cls: "green",  text: "[ BOOT ] Connecting to intelligence backend..." },
  { t: 3300, cls: "ok",     text: "[ OK   ] Backend connection established" },
  { t: 3500, cls: "green",  text: "[ BOOT ] Loading threat detection modules..." },
  { t: 3900, cls: "ok",     text: "[ OK   ] Isolation Forest model loaded" },
  { t: 4100, cls: "ok",     text: "[ OK   ] MITRE ATT&CK database indexed" },
  { t: 4300, cls: "ok",     text: "[ OK   ] Port intelligence database loaded" },
  { t: 4500, cls: "green",  text: "[ BOOT ] Verifying system integrity..." },
  { t: 4900, cls: "ok",     text: "[ OK   ] System integrity verified — SHA256 checksum passed" },
  { t: 5100, cls: "green",  text: "[ BOOT ] Starting access control layer..." },
  { t: 5400, cls: "ok",     text: "[ OK   ] Role-based access control active" },
  { t: 5600, cls: "ok",     text: "[ OK   ] Session encryption enabled" },
  { t: 5800, cls: "ok",     text: "[ OK   ] Audit logging started" },
  { t: 6000, cls: "green",  text: "[ BOOT ] Launching user interface..." },
  { t: 6400, cls: "ok",     text: "[ OK   ] Dashboard renderer initialized" },
  { t: 6600, cls: "ok",     text: "[ OK   ] Chart engine loaded" },
  { t: 6800, cls: "ok",     text: "[ OK   ] Alert feed ready" },
  { t: 7000, cls: "dim",    text: "────────────────────────────────────────────────────" },
  { t: 7100, cls: "PROGRESS", label: "[ BOOT ] System startup", duration: 2000 },
  { t: 9200, cls: "dim",    text: "────────────────────────────────────────────────────" },
  { t: 9400, cls: "yellow", text: "[ WARN ] 2 services running in limited trial mode" },
  { t: 9600, cls: "white",  text: "[ INFO ] All core systems operational" },
  { t: 9800, cls: "cyan",   text: "[ INFO ] NexGen v1.1.5.1 ready" },
  { t: 10000,cls: "dim",    text: "────────────────────────────────────────────────────" },
  { t: 10200,cls: "white",  text: "Redirecting to secure login..." },
];

const LINE_COLORS = {
  white:  "#ffffff",
  grey:   "#555555",
  dim:    "#003300",
  green:  "#00FF41",
  ok:     "#00FF41",
  cyan:   "#00D4FF",
  yellow: "#FFD700",
  red:    "#FF3D57",
  warn:   "#FFB800",
};

// ── Terminal Component ─────────────────────────────────────────────────────────
function TerminalBoot({ onDone }) {
  const [visibleLines, setVisibleLines] = useState([]);
  const [progress, setProgress]         = useState(null); // {label, pct}
  const bottomRef = useRef();

  useEffect(() => {
    let cancelled = false;
    let lineIndex  = 0;
    let timeouts   = [];

    const scheduleNext = () => {
      if (cancelled || lineIndex >= BOOT_LINES.length) return;
      const line  = BOOT_LINES[lineIndex];
      const prev  = lineIndex > 0 ? BOOT_LINES[lineIndex - 1] : { t: 0 };
      const delay = line.t - prev.t;
      lineIndex++;

      const tid = setTimeout(async () => {
        if (cancelled) return;

        if (line.cls === "PROGRESS") {
          // Animated progress bar
          let pct = 0;
          setProgress({ label: line.label, pct: 0 });
          const steps   = line.duration / 50;
          const stepAmt = 100 / steps;

          await new Promise(resolve => {
            const iv = setInterval(() => {
              if (cancelled) { clearInterval(iv); resolve(); return; }
              pct = Math.min(100, pct + stepAmt + Math.random() * stepAmt * 0.4);
              const p = Math.floor(pct);
              setProgress({ label: line.label, pct: p });
              if (pct >= 100) {
                clearInterval(iv);
                setProgress(null);
                setVisibleLines(prev => [...prev, { cls: "ok", text: "[ OK   ] Startup complete — 100%" }]);
                resolve();
              }
            }, 50);
          });
          scheduleNext();
        } else {
          setVisibleLines(prev => [...prev, { cls: line.cls, text: line.text }]);
          // Last line — wait then call onDone
          if (lineIndex >= BOOT_LINES.length) {
            setTimeout(() => { if (!cancelled) onDone(); }, 2500);
          } else {
            scheduleNext();
          }
        }
      }, delay);
      timeouts.push(tid);
    };

    scheduleNext();
    return () => {
      cancelled = true;
      timeouts.forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [visibleLines, progress]);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 99998,
      background: "#000000",
      fontFamily: "'Share Tech Mono', 'Courier New', monospace",
      fontSize: 13, lineHeight: 1.7,
      padding: "28px 32px",
      overflowY: "auto"
    }}>
      {visibleLines.map((line, i) => (
        <div key={i} style={{ color: LINE_COLORS[line.cls] || "#00FF41", whiteSpace: "pre" }}>
          {line.text}
        </div>
      ))}

      {/* Progress bar */}
      {progress && (
        <div style={{ margin: "6px 0 4px" }}>
          <div style={{ color: "#00FF41", marginBottom: 4 }}>{progress.label}</div>
          <div style={{ width: "100%", maxWidth: 500, height: 14, background: "#001100", border: "1px solid #00FF41" }}>
            <div style={{ height: "100%", width: `${progress.pct}%`, background: "#00FF41", transition: "width 0.05s linear" }} />
          </div>
          <div style={{ color: "#00FF41", marginTop: 2 }}>{progress.pct}%</div>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
}

// ── Main Login Component ───────────────────────────────────────────────────────
export default function Login() {
  const navigate = useNavigate();

  // Stages: "warning" → "terminal" → "login"
  const [stage, setStage]           = useState("warning");
  const [warningChecked, setWarningChecked] = useState(false);
  const [username, setUsername]     = useState("");
  const [password, setPassword]     = useState("");
  const [error, setError]           = useState("");
  const [loading, setLoading]       = useState(false);
  const [showPass, setShowPass]     = useState(false);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = globalStyles + `
      @import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&display=swap');
    `;
    document.head.appendChild(style);
    if (sessionStorage.getItem("nexgen_user")) navigate("/");
  }, []);

  const switchTheme = (mode) => {
    setNexGenThemeMode(mode);
    window.location.reload();
  };

  const ThemeSwitch = () => (
    <select
      value={theme.mode}
      onChange={e => switchTheme(e.target.value)}
      title="Select theme"
      style={{
        position: "fixed", top: 18, right: 18, zIndex: 20,
        width: 132,
        padding: "8px 10px",
        background: theme.colors.inputBg,
        border: `1px solid ${theme.colors.cardBorder}`,
        borderRadius: 8,
        color: theme.colors.textSecondary,
        fontFamily: theme.fonts.mono,
        fontSize: 10,
        letterSpacing: 1,
        cursor: "pointer",
        backdropFilter: "blur(10px)",
        outline: "none"
      }}
    >
      {themeOptions.map(option => (
        <option key={option.mode} value={option.mode}>{option.label}</option>
      ))}
    </select>
  );

  const handleLogin = (e) => {
    e.preventDefault();
    if (!username.trim() || !password) { setError("Please enter username and password"); return; }
    setLoading(true);
    setError("");
    setTimeout(() => {
      const result = authenticate(username.trim(), password);
      if (!result) {
        setError("Invalid credentials. Access denied.");
        setLoading(false);
      } else if (result.expired) {
        setError("This account has expired. Contact the administrator.");
        setLoading(false);
      } else {
        sessionStorage.setItem("nexgen_user", JSON.stringify({
          id: result.id, username: result.username,
          role: result.role, loginAt: new Date().toISOString(),
        }));
        navigate("/");
      }
    }, 700);
  };

  const inputStyle = {
    width: "100%", padding: "12px 14px",
    background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`,
    borderRadius: 8, color: theme.colors.textPrimary,
    fontFamily: theme.fonts.mono, fontSize: 14, outline: "none"
  };

  // ── STAGE: WARNING ─────────────────────────────────────────────────────────
  if (stage === "warning") {
    return (
      <div style={{ minHeight: "100vh", background: theme.colors.bg, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
        <ThemeSwitch />
        <div style={{ position: "absolute", inset: 0, backgroundImage: `linear-gradient(${theme.colors.bgGrid} 1px, transparent 1px), linear-gradient(90deg, ${theme.colors.bgGrid} 1px, transparent 1px)`, backgroundSize: "40px 40px" }} />

        <div style={{ position: "relative", background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderTop: "3px solid #FF3D57", borderRadius: 12, padding: "32px 36px", maxWidth: 520, width: "calc(100% - 40px)", boxShadow: theme.colors.shadow }}>

          {/* Title */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,61,87,0.15)", border: "1px solid rgba(255,61,87,0.4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "#FF3D57", flexShrink: 0 }}>⚠</div>
            <div style={{ fontFamily: theme.fonts.display, fontSize: 14, fontWeight: 700, color: "#FF3D57", letterSpacing: 2 }}>SYSTEM ACCESS WARNING</div>
          </div>

          {/* Body */}
          <div style={{ fontFamily: theme.fonts.mono, fontSize: 12, color: theme.colors.textSecondary, lineHeight: 1.9, background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 8, padding: "16px 18px", marginBottom: 20 }}>
            This system is the <span style={{ color: "#FF3D57", fontWeight: 700 }}>PRIVATE PROPERTY</span> of{" "}
            <span style={{ color: theme.colors.accent }}>Hassaan Alrifai</span>.<br />
            Unauthorized access is strictly prohibited.<br />
            <br />
            <span style={{ color: theme.colors.warning }}>All activities on this system are monitored and recorded.</span><br />
            By proceeding you consent to monitoring and confirm<br />
            you are an authorized user of this system.<br />
            <br />
            Unauthorized use may result in civil and/or criminal<br />
            prosecution under applicable local and international law.
          </div>

          {/* Checkbox */}
          <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24, cursor: "pointer", fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted }}>
            <input type="checkbox" checked={warningChecked} onChange={e => setWarningChecked(e.target.checked)}
              style={{ accentColor: theme.colors.accent, width: 15, height: 15, cursor: "pointer" }} />
            I have read and agree to the terms above
          </label>

          {/* Buttons */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <button onClick={() => { window.location.href = "https://www.google.com"; }}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", background: "rgba(255,61,87,0.1)", border: "1px solid rgba(255,61,87,0.35)", borderRadius: 7, color: "#FF3D57", fontFamily: theme.fonts.display, fontWeight: 700, fontSize: 11, letterSpacing: 1, cursor: "pointer" }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="#FF3D57" strokeWidth="1.5"/>
                <line x1="3" y1="8" x2="13" y2="8" stroke="#FF3D57" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              DENY ACCESS
            </button>

            <button onClick={() => { if (warningChecked) setStage("terminal"); }}
              disabled={!warningChecked}
              style={{ padding: "10px 24px", background: warningChecked ? `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.accentDim})` : theme.colors.accentGlow, border: "none", borderRadius: 7, color: warningChecked ? theme.colors.buttonText : theme.colors.textMuted, fontFamily: theme.fonts.display, fontWeight: 700, fontSize: 11, letterSpacing: 1, cursor: warningChecked ? "pointer" : "not-allowed", transition: "all 0.2s" }}>
              I ACCEPT →
            </button>
          </div>

          <div style={{ marginTop: 20, textAlign: "center", fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted }}>
            hassaanalrifai.com · contact@hassaanalrifai.com · © {new Date().getFullYear()}
          </div>
        </div>
      </div>
    );
  }

  // ── STAGE: TERMINAL ────────────────────────────────────────────────────────
  if (stage === "terminal") {
    return <TerminalBoot onDone={() => setStage("login")} />;
  }

  // ── STAGE: LOGIN ───────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: theme.colors.bg, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
      <ThemeSwitch />
      <div style={{ position: "absolute", inset: 0, backgroundImage: `linear-gradient(${theme.colors.bgGrid} 1px, transparent 1px), linear-gradient(90deg, ${theme.colors.bgGrid} 1px, transparent 1px)`, backgroundSize: "40px 40px" }} />
      <div style={{ position: "absolute", width: 500, height: 500, borderRadius: "50%", background: `radial-gradient(circle, ${theme.colors.accentGlow} 0%, transparent 70%)`, top: "10%", left: "50%", transform: "translateX(-50%)" }} />

      <div style={{ position: "relative", width: 420, background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 16, padding: "48px 40px", boxShadow: theme.colors.shadow }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ fontFamily: theme.fonts.display, fontSize: 38, fontWeight: 900, letterSpacing: 4 }}>
            <span style={{ color: theme.colors.accent }}>NEX</span>
            <span style={{ color: theme.colors.brandText }}>GEN</span>
          </div>
          <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textSecondary, marginTop: 6, letterSpacing: 2 }}>CYBER INTELLIGENCE PLATFORM</div>
          <div style={{ fontFamily: theme.fonts.mono, fontSize: 10, marginTop: 3, letterSpacing: 1 }}>
            <span style={{ color: theme.colors.brandText }}>by Hassaan </span>
            <span style={{ color: theme.colors.accent }}>Alrifai</span>
          </div>
          <div style={{ width: 60, height: 2, background: `linear-gradient(90deg, transparent, ${theme.colors.accent}, transparent)`, margin: "14px auto 0" }} />
        </div>

        <div style={{ fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted, textAlign: "center", marginBottom: 24, letterSpacing: 2 }}>■ SECURE ACCESS REQUIRED ■</div>

        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, letterSpacing: 2, marginBottom: 6 }}>USERNAME</label>
            <input value={username} onChange={e => setUsername(e.target.value)} autoComplete="username" style={inputStyle}
              onFocus={e => e.target.style.borderColor = theme.colors.accent}
              onBlur={e => e.target.style.borderColor = theme.colors.cardBorder} />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: "block", fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, letterSpacing: 2, marginBottom: 6 }}>PASSWORD</label>
            <div style={{ position: "relative" }}>
              <input type={showPass ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                autoComplete="current-password" style={{ ...inputStyle, paddingRight: 44 }}
                onFocus={e => e.target.style.borderColor = theme.colors.accent}
                onBlur={e => e.target.style.borderColor = theme.colors.cardBorder} />
              <button type="button" onClick={() => setShowPass(!showPass)}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: theme.colors.textMuted, cursor: "pointer", fontSize: 14, padding: 0 }}>
                {showPass ? "🙈" : "👁"}
              </button>
            </div>
          </div>

          {error && (
            <div style={{ padding: "10px 14px", background: "rgba(255,61,87,0.1)", border: "1px solid rgba(255,61,87,0.3)", borderRadius: 6, color: theme.colors.danger, fontFamily: theme.fonts.mono, fontSize: 12, marginBottom: 16, textAlign: "center" }}>
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} style={{
            width: "100%", padding: "14px",
            background: loading ? "rgba(0,212,255,0.2)" : `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.accentDim})`,
            border: "none", borderRadius: 8, color: theme.colors.buttonText,
            fontFamily: theme.fonts.display, fontWeight: 700, fontSize: 13, letterSpacing: 2,
            cursor: loading ? "not-allowed" : "pointer",
            boxShadow: loading ? "none" : "0 0 20px rgba(0,212,255,0.3)"
          }}>{loading ? "AUTHENTICATING..." : "ACCESS SYSTEM"}</button>
        </form>

        <div style={{ marginTop: 24, textAlign: "center", fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted }}>
          © {new Date().getFullYear()} hassaanalrifai.com · Unauthorized access prohibited
        </div>
      </div>
    </div>
  );
}
